import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card, Table, Tag, Typography, Space, DatePicker, Empty, Row, Col, theme, Tooltip, Segmented, Spin, Modal, Drawer,
} from '../../ui';
import { MapPin } from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
} from 'recharts';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../../lib/mapTiles';
import L from 'leaflet';
import dayjs, { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { formatIst, formatIstDate } from '../../lib/time';

delete (L.Icon.Default.prototype as any)._getIconUrl;
const dot = (color: string) => L.divIcon({
  className: '',
  html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`,
  iconSize: [14, 14], iconAnchor: [7, 7],
});

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const num = (v: any) => Number(v ?? 0).toLocaleString('en-IN');

type Preset = 'today' | 'week' | 'month' | 'year' | 'custom';
function presetRange(p: Preset, custom: [Dayjs, Dayjs] | null): [string, string] {
  const today = dayjs();
  switch (p) {
    case 'today': return [today.format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
    case 'week': return [today.startOf('week').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
    case 'year': return [today.startOf('year').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
    case 'custom': return custom && custom[0] && custom[1] ? [custom[0].format('YYYY-MM-DD'), custom[1].format('YYYY-MM-DD')] : [today.startOf('month').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
    case 'month':
    default: return [today.startOf('month').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
  }
}

export function SalesPerformancePage() {
  const { token } = theme.useToken();
  const [preset, setPreset] = useState<Preset>('month');
  const [custom, setCustom] = useState<[Dayjs, Dayjs] | null>(null);
  const [drillId, setDrillId] = useState<string | null>(null);
  const [routeSp, setRouteSp] = useState<{ id: string; name: string } | null>(null);

  const [from, to] = useMemo(() => presetRange(preset, custom), [preset, custom]);

  const { data, isFetching } = useQuery({
    queryKey: ['performance', 'sales', from, to],
    queryFn: () => request<any>({ url: '/performance/sales', params: { from, to } }),
  });
  const rows = data?.rows ?? [];
  const totals = data?.totals;

  const Kpi = ({ label, value, color, bg }: { label: string; value: string; color: string; bg: string }) => (
    <Card density="compact" style={{ borderLeft: `4px solid ${color}`, background: bg, height: '100%' }}>
      <div className="text-xs" style={{ color: token.colorTextSecondary }}>{label}</div>
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
    </Card>
  );

  const columns: any[] = [
    { title: 'Sales Person', dataIndex: 'name', fixed: 'left', width: 180,
      render: (v: string) => <span style={{ fontWeight: 600, color: token.colorPrimary }}>{v}</span> },
    { title: 'Orders', dataIndex: 'orders', align: 'right', width: 90, sorter: (a: any, b: any) => a.orders - b.orders, defaultSortOrder: 'descend' as const },
    { title: 'Order value', dataIndex: 'orderValue', align: 'right', width: 130, sorter: (a: any, b: any) => a.orderValue - b.orderValue, render: (v: any) => inr(v) },
    { title: 'Realized rev.', dataIndex: 'realizedRevenue', align: 'right', width: 130, sorter: (a: any, b: any) => a.realizedRevenue - b.realizedRevenue, render: (v: any) => <b style={{ color: '#1F8A4C' }}>{inr(v)}</b> },
    { title: 'Avg order', dataIndex: 'avgOrderValue', align: 'right', width: 110, render: (v: any) => inr(v) },
    { title: 'Visits', key: 'visits', align: 'right', width: 110, sorter: (a: any, b: any) => a.visits - b.visits,
      render: (_: any, r: any) => <Tooltip title="Completed / total"><span>{num(r.visitsCompleted)} / {num(r.visits)}</span></Tooltip> },
    { title: 'Conv. %', dataIndex: 'conversionPct', align: 'right', width: 90, sorter: (a: any, b: any) => a.conversionPct - b.conversionPct,
      render: (v: number) => <Tag color={v >= 50 ? 'green' : v >= 25 ? 'gold' : 'default'}>{v}%</Tag> },
    { title: 'Present', dataIndex: 'daysPresent', align: 'right', width: 90, sorter: (a: any, b: any) => a.daysPresent - b.daysPresent },
    { title: 'Half / Abs', key: 'ha', align: 'right', width: 100, render: (_: any, r: any) => <span style={{ color: token.colorTextTertiary }}>{r.halfDays} / {r.absentDays}</span> },
    { title: 'Attend. %', dataIndex: 'attendancePct', align: 'right', width: 100, sorter: (a: any, b: any) => a.attendancePct - b.attendancePct,
      render: (v: number) => <Tag color={v >= 90 ? 'green' : v >= 70 ? 'gold' : 'red'}>{v}%</Tag> },
    { title: 'New retailers', dataIndex: 'newRetailers', align: 'right', width: 120 },
    { title: 'New farmers', dataIndex: 'newFarmers', align: 'right', width: 110 },
    { title: 'Last location', key: 'lastLoc', width: 120, fixed: 'right' as const,
      render: (_: any, r: any) => (
        <a onClick={(e) => { e.stopPropagation(); setRouteSp({ id: r.salesPersonId, name: r.name }); }}>
          <MapPin size={16} /> Route
        </a>
      ) },
  ];

  return (
    <div>
      <Typography.Title level={4} style={{ marginBottom: 2 }}>Sales Performance</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Orders, revenue, visits and attendance per sales person. Pick a period to compare any day, month, year or custom range.
      </Typography.Paragraph>

      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          value={preset}
          onChange={(v) => setPreset(v as Preset)}
          options={[
            { label: 'Today', value: 'today' }, { label: 'This week', value: 'week' },
            { label: 'This month', value: 'month' }, { label: 'This year', value: 'year' },
            { label: 'Custom', value: 'custom' },
          ]}
        />
        {preset === 'custom' && (
          <DatePicker.RangePicker disabledDate={disableFuture}
            value={custom as any}
            onChange={(v) => setCustom(v as [Dayjs, Dayjs] | null)}
            allowClear={false}
          />
        )}
        <Typography.Text type="secondary" className="text-xs">{formatIstDate(from)} → {formatIstDate(to)}</Typography.Text>
      </Space>

      {totals && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6} lg={4}><Kpi label="Sales persons" value={num(totals.salesPersons)} color="#1677FF" bg="#E6F4FF" /></Col>
          <Col xs={12} md={6} lg={4}><Kpi label="Orders" value={num(totals.orders)} color="#722ED1" bg="#F4ECFF" /></Col>
          <Col xs={12} md={6} lg={4}><Kpi label="Order value" value={inr(totals.orderValue)} color="#D48806" bg="#FFFBE6" /></Col>
          <Col xs={12} md={6} lg={4}><Kpi label="Realized revenue" value={inr(totals.realizedRevenue)} color="#1F8A4C" bg="#F6FFED" /></Col>
          <Col xs={12} md={6} lg={4}><Kpi label="Visits done" value={num(totals.visitsCompleted)} color="#08979C" bg="#E6FFFB" /></Col>
          <Col xs={12} md={6} lg={4}><Kpi label="New customers" value={num((totals.newRetailers ?? 0) + (totals.newFarmers ?? 0))} color="#C41D7F" bg="#FFF0F6" /></Col>
        </Row>
      )}

      <Card density="compact">
        <Table
          rowKey="salesPersonId"
          size="small"
          loading={isFetching}
          dataSource={rows}
          columns={columns}
          scroll={{ x: 1400 }}
          onRow={(r: any) => ({ onClick: () => setDrillId(r.salesPersonId), style: { cursor: 'pointer' } })}
          pagination={{ defaultPageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} sales persons` }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No sales persons in scope" /> }}
        />
      </Card>

      {drillId && <PerfDrawer spId={drillId} from={from} to={to} onClose={() => setDrillId(null)} />}
      {routeSp && <RouteModal spId={routeSp.id} name={routeSp.name} from={from} to={to} onClose={() => setRouteSp(null)} />}
    </div>
  );
}

function PerfDrawer({ spId, from, to, onClose }: { spId: string; from: string; to: string; onClose: () => void }) {
  const { token } = theme.useToken();
  const { data, isLoading } = useQuery({
    queryKey: ['performance', 'sales', spId, from, to],
    queryFn: () => request<any>({ url: `/performance/sales/${spId}`, params: { from, to } }),
  });
  const m = data?.metrics;
  const daily = data?.daily ?? [];

  const Stat = ({ label, value }: { label: string; value: string }) => (
    <Col xs={12} md={8}>
      <div className="text-xs" style={{ color: token.colorTextSecondary }}>{label}</div>
      <div className="text-2xl font-bold">{value}</div>
    </Col>
  );

  return (
    <Drawer open width={680} onClose={onClose} title={<span>Performance · <b>{data?.name ?? ''}</b></span>}>
      {isLoading ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div> : !m ? <Empty description="No data" /> : (
        <>
          <Typography.Text type="secondary" className="text-xs">{formatIstDate(from)} → {formatIstDate(to)}</Typography.Text>
          <Row gutter={[16, 16]} style={{ margin: '12px 0 20px' }}>
            <Stat label="Orders" value={num(m.orders)} />
            <Stat label="Order value" value={inr(m.orderValue)} />
            <Stat label="Realized revenue" value={inr(m.realizedRevenue)} />
            <Stat label="Avg order" value={inr(m.avgOrderValue)} />
            <Stat label="Visits (done/total)" value={`${num(m.visitsCompleted)} / ${num(m.visits)}`} />
            <Stat label="Conversion" value={`${m.conversionPct}%`} />
            <Stat label="Days present" value={num(m.daysPresent)} />
            <Stat label="Half / Absent" value={`${m.halfDays} / ${m.absentDays}`} />
            <Stat label="Attendance" value={`${m.attendancePct}%`} />
            <Stat label="New retailers" value={num(m.newRetailers)} />
            <Stat label="New farmers" value={num(m.newFarmers)} />
          </Row>

          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Daily order value</Typography.Text>
          <div style={{ height: 200, marginBottom: 20 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={daily} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => dayjs(d).format('DD/MM')} fontSize={11} />
                <YAxis fontSize={11} width={48} />
                <RTooltip formatter={(v: any) => inr(v)} labelFormatter={(d) => formatIstDate(d)} />
                <Area type="monotone" dataKey="value" stroke="#1F8A4C" fill="#D9F7BE" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Daily visits</Typography.Text>
          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => dayjs(d).format('DD/MM')} fontSize={11} />
                <YAxis allowDecimals={false} fontSize={11} width={32} />
                <RTooltip labelFormatter={(d) => formatIstDate(d)} />
                <Bar dataKey="visits" fill="#1677FF" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </Drawer>
  );
}

// Fits the map to all points + fixes sizing once the modal has opened.
function MapReady({ pts }: { pts: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => {
      map.invalidateSize();
      if (pts.length === 1) map.setView(pts[0], 15);
      else if (pts.length > 1) map.fitBounds(pts as any, { padding: [30, 30], maxZoom: 16 });
    }, 250);
    return () => clearTimeout(t);
  }, [map, pts]);
  return null;
}

function RouteModal({ spId, name, from, to, onClose }: { spId: string; name: string; from: string; to: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['perf-route', spId, from, to],
    queryFn: () => request<any>({ url: `/performance/sales/${spId}/route`, params: { from, to } }),
  });
  const trace = data?.trace ?? [];
  const visits = data?.visits ?? [];
  const attendance = data?.attendance ?? [];
  const last = data?.lastLocation;
  const tracePts: [number, number][] = trace.map((t: any) => [t.lat, t.lng]);
  const allPts: [number, number][] = useMemo(() => [
    ...tracePts,
    ...visits.map((v: any) => [v.lat, v.lng] as [number, number]),
    ...attendance.map((a: any) => [a.lat, a.lng] as [number, number]),
  ], [data]);

  return (
    <Modal open width={840} onOpenChange={(o) => { if (!o) onClose(); }}
      title={<span><MapPin size={16} /> Route · <b>{name}</b> <Typography.Text type="secondary" className="text-xs" style={{ fontWeight: 400 }}>{formatIstDate(from)} → {formatIstDate(to)}</Typography.Text></span>}>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
      ) : allPts.length === 0 ? (
        <Empty description="No location data for this period (no GPS pings, visits or geo-tagged attendance)." />
      ) : (
        <>
          {last && (
            <Typography.Paragraph style={{ marginBottom: 8 }}>
              <b>Last seen:</b> {last.label} · {formatIst(last.at)}{' '}
              <a href={`https://www.google.com/maps?q=${last.lat},${last.lng}`} target="_blank" rel="noreferrer">open in maps ↗</a>
            </Typography.Paragraph>
          )}
          <div style={{ height: 460, borderRadius: 8, overflow: 'hidden' }}>
            <MapContainer center={[22.5, 80]} zoom={5} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
              <TileLayer url={MAP_TILE_URL} attribution={MAP_TILE_ATTRIBUTION} />
              <MapReady pts={allPts} />
              {tracePts.length > 1 && <Polyline positions={tracePts} pathOptions={{ color: '#1677FF', weight: 3, opacity: 0.8 }} />}
              {visits.map((v: any, i: number) => (
                <Marker key={`v${i}`} position={[v.lat, v.lng]} icon={dot('#1F8A4C')}>
                  <Popup><b>{v.name}</b><br />Visit · {formatIst(v.at)}</Popup>
                </Marker>
              ))}
              {attendance.map((a: any, i: number) => (
                <Marker key={`a${i}`} position={[a.lat, a.lng]} icon={dot('#722ED1')}>
                  <Popup>Attendance {a.kind}<br />{formatIst(a.at)}</Popup>
                </Marker>
              ))}
              {last && <Marker position={[last.lat, last.lng]} icon={dot('#D9363E')}><Popup><b>Last seen</b><br />{last.label}<br />{formatIst(last.at)}</Popup></Marker>}
            </MapContainer>
          </div>
          <Space size={16} className="text-xs" style={{ marginTop: 8 }} wrap>
            <span><span style={{ color: '#1677FF' }}>▬</span> GPS route ({trace.length})</span>
            <span><span style={{ color: '#1F8A4C' }}>●</span> Visits ({visits.length})</span>
            <span><span style={{ color: '#722ED1' }}>●</span> Attendance ({attendance.length})</span>
            <span><span style={{ color: '#D9363E' }}>●</span> Last seen</span>
          </Space>
        </>
      )}
    </Modal>
  );
}
